import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { User } from 'lucide-react';
import { auth } from '../../config/firebaseConfig';
import BottomNavigation from './BottomNavigation';

import MobileScheduleEdit from './MobileScheduleEdit';
import ChatBox from '../chat/ChatBox';
import EventDetailModal, { MapModal } from './EventDetailModal';
import CustomAlertModal from '../modals/CustomAlertModal';

// 분할된 모듈
import useCalendarData from './calendarView/hooks/useCalendarData';
import { createCalendarHandlers } from './calendarView/hooks/useCalendarHandlers';
import { useVoiceRecognition } from './calendarView/hooks/useVoiceRecognition';
import { getEventsForDate } from './calendarView/utils/eventUtils';
import BottomSection from './calendarView/components/BottomSection';
import CalendarContent from './calendarView/components/CalendarContent';
import MultiMonthScrollView from './calendarView/components/MultiMonthScrollView';
import EventEditPanel from './calendarView/components/EventEditPanel';
import CalendarHeader from './calendarView/components/CalendarHeader';

import './styles/MobileCalendarView.css';
import { API_BASE_URL } from '../../config/apiConfig';

const MobileCalendarView = ({ user, setIsVoiceEnabled }) => {
   const navigate = useNavigate();
   const [searchParams, setSearchParams] = useSearchParams();
   const calendarRef = useRef(null);

   const [showScheduleEdit, setShowScheduleEdit] = useState(false);
   const [isChatOpen, setIsChatOpen] = useState(searchParams.get('chat') === 'open');
   const [isEditing, setIsEditing] = useState(false);
   const [initialState, setInitialState] = useState(null);
   const [currentTitle, setCurrentTitle] = useState(() => {
      const now = new Date();
      return `${now.getFullYear()}년 ${now.getMonth() + 1}월`;
   });
   const [calendarView, setCalendarView] = useState('dayGridMonth');
   const [selectedDate, setSelectedDate] = useState(new Date());

   const [selectedEvent, setSelectedEvent] = useState(null);
   const [showMapModal, setShowMapModal] = useState(false);
   const [selectedLocation, setSelectedLocation] = useState(null);

   const [toastMessage, setToastMessage] = useState(null);
   const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
   const showToast = useCallback((msg) => { setToastMessage(msg); }, []);

   const [isSidebarOpen, setIsSidebarOpen] = useState(false);

   // 마이크 음성 인식 상태
   const [voiceTranscript, setVoiceTranscript] = useState('');
   const [isVoiceListening, setIsVoiceListening] = useState(false);
   const [isVoiceAnalyzing, setIsVoiceAnalyzing] = useState(false);

   // 보기 모드: 'stack'(스택형) | 'detail'(상세형)
   const [displayMode, setDisplayMode] = useState(() => {
      const saved = localStorage.getItem('calDisplayMode');
      return (saved === 'stack' || saved === 'detail') ? saved : 'stack';
   });
   const changeDisplayMode = useCallback((mode) => {
      setDisplayMode(mode);
      localStorage.setItem('calDisplayMode', mode);
   }, []);

   // 날짜 탭 → 하단 일정 패널
   const [showDateDetail, setShowDateDetail] = useState(false);

   const handleCalendarScroll = useCallback(() => {
      setShowDateDetail(false);
   }, []);

   // 스크롤 시 현재 보이는 월로 헤더 업데이트
   const handleVisibleMonthChange = useCallback(({ year, month }) => {
      setCurrentTitle(`${year}년 ${month + 1}월`);
   }, [setCurrentTitle]);

   // --- 데이터 훅 ---
   const {
      defaultSchedule, setDefaultSchedule,
      scheduleExceptions, setScheduleExceptions,
      personalTimes, setPersonalTimes,
      events, setEvents,
      googleCalendarEvents, setGoogleCalendarEvents,
      isLoading,
      globalEvents,
      visibleRangeRef,
      fetchSchedule,
      fetchGlobalEvents,
      convertScheduleToEvents,
      chatEnhanced,
      isLoggedIn,
      eventActions,
      setEventAddedKey,
   } = useCalendarData(user, calendarRef);

   // --- personalTimes에 직접 일정 저장 ---
   const addPersonalTimeDirect = useCallback(async (schedule) => {
      const API = API_BASE_URL;
      try {
         const currentUser = auth.currentUser;
         if (!currentUser) { showToast('로그인이 필요합니다.'); return; }
         const idToken = await currentUser.getIdToken();

         const startTime = schedule.time || schedule.startTime || '09:00';
         const [sh, sm] = startTime.split(':').map(Number);
         const endMin = sh * 60 + sm + (schedule.duration || 60);
         const endTime = `${String(Math.floor(endMin / 60) % 24).padStart(2, '0')}:${String(endMin % 60).padStart(2, '0')}`;
         const nowKST = new Date(Date.now() + 9 * 60 * 60 * 1000);

         const newEntry = {
            id: Date.now() + Math.random(),
            title: schedule.title || schedule.subject || '일정',
            type: 'event',
            startTime,
            endTime,
            days: [],
            isRecurring: false,
            specificDate: schedule.date || nowKST.toISOString().split('T')[0],
            color: 'bg-gray-500',
            participants: 1,
            externalParticipants: [],
            location: schedule.location || '',
         };

         const scheduleRes = await fetch(`${API}/api/users/profile/schedule`, {
            headers: { 'Authorization': `Bearer ${idToken}` }
         });
         if (!scheduleRes.ok) throw new Error('스케줄 로드 실패');
         const currentSchedule = await scheduleRes.json();
         const dbOnly = (currentSchedule.personalTimes || []).filter(pt => !pt.isGoogleEvent);

         const updateRes = await fetch(`${API}/api/users/profile/schedule`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
            body: JSON.stringify({
               defaultSchedule: currentSchedule.defaultSchedule,
               scheduleExceptions: currentSchedule.scheduleExceptions,
               personalTimes: [...dbOnly, newEntry]
            })
         });

         if (updateRes.ok) {
            showToast('✓ 일정이 추가됐습니다!');
            await fetchSchedule();
         } else {
            showToast('일정 추가에 실패했습니다.');
         }
      } catch (err) {
         console.error('[addPersonalTimeDirect]', err);
         showToast('일정 추가에 실패했습니다.');
      }
   }, [fetchSchedule, showToast]);

   // --- 음성 인식 완료 → AI 분석 ---
   const handleVoiceComplete = useCallback(async (transcript) => {
      setIsVoiceListening(false);
      setVoiceTranscript('');
      if (!transcript.trim()) return;

      setIsVoiceAnalyzing(true);
      try {
         const API = API_BASE_URL;
         const currentUser = auth.currentUser;
         if (!currentUser) { showToast('로그인이 필요합니다.'); return; }
         const idToken = await currentUser.getIdToken();

         const res = await fetch(`${API}/api/call-analysis/analyze-clipboard`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
            body: JSON.stringify({ text: transcript })
         });
         const data = await res.json();
         const info = data?.data?.extractedInfo;

         if (data?.data?.isScheduleRelated && info) {
            await addPersonalTimeDirect({
               title: info.title || '일정',
               date: info.date || '',
               time: info.time || '',
               location: info.location || '',
               description: info.description || '',
               participants: info.participants || [],
               confidence: data.data.confidence || 0.8,
               originalText: transcript,
            });
         } else {
            showToast('일정 정보를 찾을 수 없습니다. 다시 말씀해 주세요.');
         }
      } catch (err) {
         console.error('[handleVoiceComplete]', err);
         showToast('음성 분석에 실패했습니다.');
      } finally {
         setIsVoiceAnalyzing(false);
      }
   }, [showToast, addPersonalTimeDirect]);

   // --- 카메라 캡처 ---
   const cameraInputRef = useRef(null);
   const [cameraFile, setCameraFile] = useState(null);

   const handleStartCamera = useCallback(() => {
      if (cameraInputRef.current) cameraInputRef.current.click();
   }, []);

   const handleCameraCapture = useCallback((e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setCameraFile(file);
      setIsChatOpen(true);
      e.target.value = '';
   }, []);

   // --- 이벤트 핸들러 ---
   const {
      handleStartEdit, handleCancel, handleSave, handleClearAll,
      handleDateClick, handleEventClick, handleDeleteScheduleEvent,
      handleSplitItemClick, handleOpenMap, handleCloseMapModal,
      handleViewChange, handleLogout, handleChatMessage,
   } = createCalendarHandlers({
      defaultSchedule, setDefaultSchedule,
      scheduleExceptions, setScheduleExceptions,
      personalTimes, setPersonalTimes,
      events, setEvents,
      googleCalendarEvents, setGoogleCalendarEvents,
      initialState, setInitialState,
      isEditing, setIsEditing,
      calendarRef, selectedDate, setSelectedDate,
      visibleRangeRef,
      fetchSchedule, fetchGlobalEvents,
      showToast, setConfirmModal, setSelectedEvent,
      navigate,
      convertScheduleToEvents,
      user,
      isChatOpen, setIsChatOpen,
      chatEnhanced, globalEvents,
      setIsVoiceEnabled,
      setShowMapModal, setSelectedLocation,
      setCalendarView, setCurrentTitle,
   });

   // --- 음성 인식 훅 ---
   const handleVoiceEnabledChange = useCallback((enabled) => {
      setIsVoiceEnabled(enabled);
      if (!enabled) setIsVoiceListening(false);
   }, [setIsVoiceEnabled]);

   const { handleStartVoiceRecognition: _handleStartVoiceRecognition, handleStopVoiceRecognition } = useVoiceRecognition({
      showToast,
      setIsVoiceEnabled: handleVoiceEnabledChange,
      isChatOpen,
      setIsChatOpen,
      handleChatMessage,
      onTranscriptUpdate: (text) => {
         setVoiceTranscript(text);
         setIsVoiceListening(true);
      },
      onVoiceComplete: handleVoiceComplete,
   });

   const handleStartVoiceRecognition = useCallback(() => {
      setVoiceTranscript('');
      setIsVoiceListening(true);
      _handleStartVoiceRecognition();
   }, [_handleStartVoiceRecognition]);

   const handleToggleMic = useCallback(() => {
      if (isVoiceListening) {
         handleStopVoiceRecognition();
         setIsVoiceListening(false);
      } else {
         handleStartVoiceRecognition();
      }
   }, [isVoiceListening, handleStopVoiceRecognition, handleStartVoiceRecognition]);

   // 날짜 탭: selectedDate 설정 + 하단 패널 열기
   const handleDateClickWithDetail = useCallback((info) => {
      handleDateClick(info);
      setShowDateDetail(true);
   }, [handleDateClick]);

   // --- 새로고침 핸들러 ---
   const handleRefresh = useCallback(async () => {
      try {
         await fetchSchedule();
         showToast('새로고침 완료');
      } catch {
         showToast('새로고침에 실패했습니다.');
      }
   }, [fetchSchedule, showToast]);

   // --- 쿼리 파라미터 처리 ---
   useEffect(() => {
      if (searchParams.get('chat') === 'open') {
         searchParams.delete('chat');
         setSearchParams(searchParams, { replace: true });
      }
   }, []);

   useEffect(() => {
      if (searchParams.get('voice') === 'start') {
         searchParams.delete('voice');
         setSearchParams(searchParams, { replace: true });
         setTimeout(() => handleStartVoiceRecognition(), 500);
      }
   }, []);

   useEffect(() => {
      if (searchParams.get('camera') === 'open') {
         searchParams.delete('camera');
         setSearchParams(searchParams, { replace: true });
         setTimeout(() => handleStartCamera(), 500);
      }
   }, []);

   // --- 렌더링 ---
   if (showScheduleEdit) return <MobileScheduleEdit onBack={() => setShowScheduleEdit(false)} />;

   const showLegend = googleCalendarEvents.length > 0;
   const dayEvents = getEventsForDate(selectedDate, events)
      .sort((a, b) => new Date(a.start) - new Date(b.start));

   return (
      <div className={`mobile-calendar-view view-${calendarView} ${calendarView === 'timeGridDay' ? 'split-mode' : ''}`}>
        <CalendarHeader
          user={user}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          handleLogout={handleLogout}
        />

         <div className="schedule-content" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {isLoading ? (
               <div className="loading-state" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
                  {localStorage.getItem('userProfileImage') ? (
                     <img src={localStorage.getItem('userProfileImage')} alt="로딩 중" style={{ width: '150px', height: '150px', borderRadius: '50%', objectFit: 'cover', marginBottom: '16px' }} />
                  ) : (
                     <div style={{ width: '150px', height: '150px', borderRadius: '50%', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                        <User size={80} color="#9ca3af" />
                     </div>
                  )}
                  <p style={{ color: '#666', fontSize: '14px' }}>로딩 중...</p>
               </div>
            ) : (
               <>
                  {/* 편집 패널 (삼선 + 범례 + 편집버튼) */}
                  <EventEditPanel
                     currentTitle={currentTitle}
                     isEditing={isEditing}
                     handleStartEdit={handleStartEdit}
                     handleCancel={handleCancel}
                     handleClearAll={handleClearAll}
                     handleSave={handleSave}
                     displayMode={displayMode}
                     onDisplayModeChange={changeDisplayMode}
                     showLegend={showLegend}
                  />

                  {/* 공통 달력: 항상 MultiMonthScrollView */}
                  <MultiMonthScrollView
                     events={events}
                     displayMode={displayMode}
                     onDateClick={handleDateClickWithDetail}
                     onEventClick={handleEventClick}
                     selectedDate={selectedDate}
                     onScroll={handleCalendarScroll}
                     onVisibleMonthChange={handleVisibleMonthChange}
                  />

                  {/* 날짜 탭 시 하단 일정 패널 (비편집 모드) */}
                  {!isEditing && showDateDetail && (
                     <div style={{
                        flexShrink: 0, backgroundColor: '#fff',
                        borderTop: '2px solid #e5e7eb',
                        height: 310, display: 'flex', flexDirection: 'column',
                     }}>
                        {/* 헤더 */}
                        <div style={{
                           padding: '10px 16px 8px', display: 'flex',
                           alignItems: 'center',
                           borderBottom: '1px solid #f3f4f6', flexShrink: 0,
                        }}>
                           <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                              {selectedDate instanceof Date && !isNaN(selectedDate)
                                 ? selectedDate.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'long' })
                                 : ''}
                           </span>
                        </div>
                        {/* 일정 목록 */}
                        <div style={{ overflowY: 'auto', flex: 1 }}>
                           {dayEvents.length === 0 ? (
                              <div style={{ padding: '20px 16px', color: '#9ca3af', fontSize: 13, textAlign: 'center' }}>
                                 이날 일정이 없습니다
                              </div>
                           ) : (
                              dayEvents.map((ev, i) => (
                                 <div
                                    key={i}
                                    onClick={() => handleEventClick({ event: ev })}
                                    style={{
                                       display: 'flex', alignItems: 'center', gap: 12,
                                       padding: '11px 16px', borderBottom: '1px solid #f9fafb', cursor: 'pointer',
                                    }}
                                 >
                                    <div style={{
                                       width: 4, height: 36, flexShrink: 0,
                                       backgroundColor: ev.backgroundColor || ev.color || '#3b82f6',
                                       borderRadius: 2,
                                    }} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                       <div style={{
                                          fontSize: 14, fontWeight: 600, color: '#111827',
                                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                       }}>
                                          {ev.title === '가능' ? '선호시간' : ev.title}
                                       </div>
                                       <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                                          {(() => {
                                             const s = new Date(ev.start);
                                             const e = new Date(ev.end);
                                             if (isNaN(s) || isNaN(e)) return '';
                                             return `${s.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false })} ~ ${e.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
                                          })()}
                                       </div>
                                    </div>
                                 </div>
                              ))
                           )}
                        </div>
                     </div>
                  )}

                  {/* 편집 모드: 선호시간/개인시간 관리 패널 */}
                  {isEditing && (
                     <BottomSection
                        isEditing={isEditing}
                        calendarView={calendarView}
                        selectedDate={selectedDate}
                        defaultSchedule={defaultSchedule}
                        personalTimes={personalTimes}
                        events={events}
                        onSplitItemClick={handleSplitItemClick}
                        displayMode="detail"
                     />
                  )}

                  {/* FullCalendar: calendarRef 유지용 (숨김) */}
                  <div style={{ display: 'none' }}>
                     <CalendarContent
                        calendarRef={calendarRef}
                        isEditing={isEditing}
                        events={events}
                        handleDateClick={handleDateClick}
                        handleEventClick={handleEventClick}
                        handleViewChange={handleViewChange}
                     />
                  </div>
               </>
            )}
         </div>

         {/* 숨겨진 카메라 입력 */}
         <input
            type="file"
            accept="image/*"
            capture="environment"
            ref={cameraInputRef}
            style={{ display: 'none' }}
            onChange={handleCameraCapture}
         />

         {/* 하단 네비게이션 바 */}
         <BottomNavigation
            onRefresh={handleRefresh}
            onChat={() => setIsChatOpen(!isChatOpen)}
            onMic={handleToggleMic}
            isVoiceListening={isVoiceListening}
            onCamera={handleStartCamera}
         />

         {/* 챗봇 */}
         {isChatOpen && (
            <ChatBox
               onSendMessage={handleChatMessage}
               currentTab="profile"
               onEventUpdate={() => { fetchSchedule(); setCameraFile(null); }}
               forceOpen={true}
               initialTimetableImages={cameraFile ? [cameraFile] : []}
               openTimetable={!!cameraFile}
            />
         )}

         {selectedEvent && <EventDetailModal event={selectedEvent} user={user} onClose={() => setSelectedEvent(null)} onOpenMap={handleOpenMap} onDelete={handleDeleteScheduleEvent} previousLocation={null} isEditing={isEditing} />}
         {showMapModal && selectedLocation && <MapModal address={selectedLocation.address} lat={selectedLocation.lat} lng={selectedLocation.lng} onClose={handleCloseMapModal} participantLocations={selectedLocation.participantLocations || []} />}

         {/* 음성 인식 중 오버레이 */}
         {(isVoiceListening || isVoiceAnalyzing) && (
            <div style={{
               position: 'fixed', bottom: 90, left: '50%', transform: 'translateX(-50%)',
               zIndex: 50000, backgroundColor: 'rgba(99,102,241,0.95)', color: '#fff',
               padding: '16px 20px', borderRadius: 16, maxWidth: '85%', width: 'fit-content',
               boxShadow: '0 4px 20px rgba(99,102,241,0.4)', textAlign: 'center',
            }}>
               {isVoiceAnalyzing ? (
                  <div>
                     <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 4 }}>AI 일정 분석 중...</div>
                     <div style={{ fontSize: 15, fontWeight: 600 }}>잠시만 기다려주세요</div>
                  </div>
               ) : (
                  <div>
                     <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f87171', animation: 'pulse 1s infinite' }} />
                        <span style={{ fontSize: 13, opacity: 0.85 }}>말씀해 주세요...</span>
                     </div>
                     {voiceTranscript && (
                        <div style={{ fontSize: 15, fontWeight: 600, marginTop: 4, maxWidth: 280 }}>
                           {voiceTranscript}
                        </div>
                     )}
                  </div>
               )}
            </div>
         )}

         {toastMessage && (
            <div
               style={{
                  position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)',
                  zIndex: 100000, backgroundColor: 'rgba(0,0,0,0.8)', color: '#fff',
                  padding: '12px 24px', borderRadius: 12, fontSize: 14, fontWeight: 500,
                  maxWidth: '85%', textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  animation: 'fadeInUp 0.3s ease-out'
               }}
               onClick={() => setToastMessage(null)}
               onAnimationEnd={() => setTimeout(() => setToastMessage(null), 2500)}
            >
               {toastMessage}
            </div>
         )}

         <CustomAlertModal
            isOpen={confirmModal.isOpen}
            onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
            onConfirm={confirmModal.onConfirm}
            title={confirmModal.title}
            message={confirmModal.message}
            type="warning"
            showCancel={true}
            confirmText="확인"
            cancelText="취소"
         />
      </div>
   );
};

export default MobileCalendarView;
